import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/* iter929 — Single source of truth for "is the user typing?". Covers
 * inputs, textareas, selects, contenteditable regions and IME composition
 * so global hotkeys can never hijack keystrokes mid-typing. */
export function isTypingTarget(e) {
  if (e && e.isComposing) return true;
  const isField = (node) => {
    if (!node || !node.tagName) return false;
    const tag = node.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (node.isContentEditable) return true;
    const role = node.getAttribute && node.getAttribute('role');
    if (role === 'combobox' || role === 'searchbox' || role === 'spinbutton' || role === 'textbox') return true;
    return false;
  };
  // iter944 — also honour the currently-focused element and any field ancestor
  // so date-pickers, comboboxes and portalled search inputs never let a global
  // hotkey hijack a keystroke while the user is typing.
  if (isField(e && e.target)) return true;
  if (isField(typeof document !== 'undefined' ? document.activeElement : null)) return true;
  try {
    const el = e && e.target;
    if (el && el.closest && el.closest('input, textarea, select, [contenteditable="true"], [contenteditable=""], [role="combobox"], [role="searchbox"], [role="textbox"]')) return true;
  } catch { /* non-element target */ }
  return false;
}

const SHORTCUTS = [
  // Navigation - Number keys
  { keys: ['ctrl', '1'], action: 'nav', path: '/dashboard', label: 'Dashboard' },
  { keys: ['ctrl', '2'], action: 'nav', path: '/sales', label: 'Sales' },
  { keys: ['ctrl', '3'], action: 'nav', path: '/expenses', label: 'Expenses' },
  { keys: ['ctrl', '4'], action: 'nav', path: '/customers', label: 'Customers' },
  { keys: ['ctrl', '5'], action: 'nav', path: '/suppliers', label: 'Suppliers' },
  { keys: ['ctrl', '6'], action: 'nav', path: '/stock', label: 'Stock' },
  { keys: ['ctrl', '7'], action: 'nav', path: '/employees', label: 'Employees' },
  { keys: ['ctrl', '8'], action: 'nav', path: '/reports', label: 'Reports' },
  { keys: ['ctrl', '9'], action: 'nav', path: '/analytics', label: 'Analytics' },
  // Navigation - Shift combos
  { keys: ['ctrl', 'shift', 'p'], action: 'nav', path: '/cashier-pos', label: 'Open POS' },
  { keys: ['ctrl', 'shift', 'k'], action: 'nav', path: '/kds', label: 'Kitchen Display' },
  { keys: ['ctrl', 'shift', 'n'], action: 'nav', path: '/notifications', label: 'Notifications' },
  { keys: ['ctrl', 'shift', 's'], action: 'nav', path: '/settings', label: 'Settings' },
  // Ctrl+Shift+R reserved for browser hard reload — do not intercept
  { keys: ['ctrl', 'shift', 'i'], action: 'nav', path: '/invoices', label: 'Invoices' },
  { keys: ['ctrl', 'shift', 'd'], action: 'nav', path: '/documents', label: 'Documents' },
  { keys: ['ctrl', 'shift', 't'], action: 'nav', path: '/transfers', label: 'Transfers' },
  // Navigation - Alt combos
  { keys: ['alt', 'p'], action: 'nav', path: '/performance-report', label: 'Performance Report' },
  { keys: ['alt', 'c'], action: 'nav', path: '/task-compliance', label: 'Task Compliance' },
  { keys: ['alt', 'v'], action: 'nav', path: '/visualizations', label: 'Visualizations' },
  { keys: ['alt', 's'], action: 'nav', path: '/schedule', label: 'Schedule' },
  { keys: ['alt', 'l'], action: 'nav', path: '/leave-approvals', label: 'Leave Approvals' },
  { keys: ['alt', 'm'], action: 'nav', path: '/menu-items', label: 'Menu Items' },
  { keys: ['alt', 'a'], action: 'nav', path: '/anomaly-detection', label: 'Anomaly Detection' },
  // Actions
  { keys: ['ctrl', 'n'], action: 'event', event: 'shortcut:new-sale', label: 'New Sale' },
  { keys: ['ctrl', 'e'], action: 'event', event: 'shortcut:new-expense', label: 'New Expense' },
  { keys: ['ctrl', 'k'], action: 'event', event: 'shortcut:search', label: 'Quick Search' },
  { keys: ['ctrl', 'f'], action: 'event', event: 'shortcut:filter', label: 'Toggle Filter' },
  { keys: ['ctrl', 'shift', 'e'], action: 'event', event: 'shortcut:export', label: 'Export Data' },
  { keys: ['ctrl', '/'], action: 'event', event: 'shortcut:help', label: 'Show Shortcuts' },
  { keys: ['escape'], action: 'event', event: 'shortcut:escape', label: 'Close Dialog' },
];

export function useKeyboardShortcuts() {
  const navigate = useNavigate();

  const handleKeyDown = useCallback((e) => {
    // iter929 — Ignore ALL shortcuts while the user is typing anywhere.
    if (isTypingTarget(e)) {
      if (e.key === 'Escape' && e.target?.blur) {
        e.target.blur();
      }
      return;
    }

    for (const shortcut of SHORTCUTS) {
      const needCtrl = shortcut.keys.includes('ctrl');
      const needShift = shortcut.keys.includes('shift');
      const needAlt = shortcut.keys.includes('alt');
      const key = shortcut.keys.filter(k => k !== 'ctrl' && k !== 'shift' && k !== 'alt')[0];

      if (needCtrl !== (e.ctrlKey || e.metaKey)) continue;
      if (needShift !== e.shiftKey) continue;
      if (needAlt !== e.altKey) continue;
      if (e.key.toLowerCase() !== key && e.key !== key) continue;

      e.preventDefault();
      e.stopPropagation();

      if (shortcut.action === 'nav') {
        navigate(shortcut.path);
      } else if (shortcut.action === 'event') {
        window.dispatchEvent(new CustomEvent(shortcut.event));
      }
      return;
    }
  }, [navigate]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return SHORTCUTS;
}

export { SHORTCUTS };
